import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@components/atoms/Typography';
import { useThemes } from '@hooks/useThemes';
import { ResultsType } from '@types';
import styles from './Pagination.module.css';

export interface PaginationProps {
    loading: boolean;
    type: ResultsType;
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

export const Pagination: FC<PaginationProps> = ({
    loading,
    type,
    currentPage = 1,
    totalPages = 1,
    onPageChange
}) => {
    const { t } = useTranslation();

    const translations = {
        endOfSpaces: t('pagination-component.end-of-spaces'),
        previous: t('pagination-component.previous') || 'Previous',
        next: t('pagination-component.next') || 'Next',
    };

    const getEndOfText: () => string = useCallback(() => {
        switch (type) {
            case ResultsType.spaces:
                return translations.endOfSpaces;
            default:
                return '';
        }
    }, [
        type,
        translations.endOfSpaces,
    ]);

    // Generate page numbers to display
    const pageNumbers = useMemo(() => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 7; // Total number of page buttons to show

        if (totalPages <= maxPagesToShow) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage <= 3) {
                // Near the beginning
                for (let i = 2; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Near the end
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // In the middle
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    }, [currentPage, totalPages]);

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        onPageChange?.(page);
    };

    const showPagination = !loading && totalPages > 1;

    return (
        <div className="d-flex flex-column align-items-center pt-4 gap-3" data-testid="pagination">
            {loading ? (
                <Loader />
            ) : showPagination ? (
                <div className={styles['pagination-container']}>
                    <nav aria-label="Page navigation">
                        <ul className="pagination mb-0">
                        {/* Previous Button */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                aria-label={translations.previous}
                            >
                                <i className="fa-solid fa-chevron-left" />
                            </button>
                        </li>

                        {/* Page Numbers */}
                        {pageNumbers.map((page, index) => {
                            if (page === '...') {
                                return (
                                    <li key={`ellipsis-${index}`} className="page-item disabled">
                                        <span className="page-link">...</span>
                                    </li>
                                );
                            }

                            const pageNum = page as number;
                            return (
                                <li
                                    key={pageNum}
                                    className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pageNum)}
                                        disabled={currentPage === pageNum}
                                    >
                                        {pageNum}
                                    </button>
                                </li>
                            );
                        })}

                        {/* Next Button */}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                aria-label={translations.next}
                            >
                                <i className="fa-solid fa-chevron-right" />
                            </button>
                        </li>
                    </ul>
                </nav>
                </div>
            ) : totalPages === 1 ? (
                <Typography classes="mb-0" color="muted">
                    <>
                        {getEndOfText()} <i className="fa-light fa-face-frown-slight" />
                    </>
                </Typography>
            ) : null}
        </div>
    );
};

export const Loader: FC = () => {
    const { useGrayscaleTheme } = useThemes();

    return (
        <div
            className={
                // theme
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles.loader}`
            }></div>
    );
};
